import React, { useState, useEffect } from 'react';
import CloseIcon from '../icons/CloseIcon';
import TextAreaField from './TextAreaField';
import Button from './Button';

const pxToRem = (value: number) => `${value / 16}rem`;

interface Student {
  uid: string;
  name: string;
  number?: number;
}

interface DistributePointsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  students: Student[];
  templateTitle?: string;
  templateDescription?: string;
  templatePointAmount?: number;
  onSave: (data: {
    selectedStudentIds: string[];
    reason: string;
    distributionDate: string;
  }) => Promise<void> | void;
}

const DistributePointsDialog: React.FC<DistributePointsDialogProps> = ({
  isOpen,
  onClose,
  students,
  templateTitle,
  templateDescription,
  templatePointAmount,
  onSave
}) => {
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [reason, setReason] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setSelectedStudentIds([]);
      setReason(templateDescription || '');
      setIsSubmitting(false);
    } else {
      setReason(templateDescription || '');
    }
  }, [isOpen, templateDescription]);

  const toggleStudent = (uid: string) => {
    setSelectedStudentIds((prev) =>
      prev.includes(uid) ? prev.filter((id) => id !== uid) : [...prev, uid]
    );
  };

  const handleSelectAll = () => {
    setSelectedStudentIds(students.map((s) => s.uid));
  };

  const handleClearSelection = () => {
    setSelectedStudentIds([]);
  };

  const handleSave = async () => {
    if (selectedStudentIds.length === 0 || !reason.trim()) {
      return;
    }

    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const todayString = `${year}-${month}-${day}`;

    setIsSubmitting(true);
    try {
      await onSave({
        selectedStudentIds,
        reason: reason.trim(),
        distributionDate: todayString
      });
      onClose();
    } catch (error) {
      console.error('Error distributing points:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSaveDisabled = selectedStudentIds.length === 0 || !reason.trim() || isSubmitting;
  const allSelected = students.length > 0 && selectedStudentIds.length === students.length;

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
        fontFamily: 'Pretendard, sans-serif'
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: pxToRem(450),
          background: 'var(--Color-background-normal, white)',
          borderRadius: pxToRem(16),
          boxShadow: '0px 0px 3px 0px rgba(0, 0, 0, 0.06), 0px 8px 16px 0px rgba(0, 0, 0, 0.12)',
          padding: `${pxToRem(24)} ${pxToRem(28)}`,
          display: 'flex',
          flexDirection: 'column',
          gap: pxToRem(40),
          maxHeight: '90vh',
          overflow: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: pxToRem(40),
            width: '100%'
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              gap: pxToRem(8),
              width: '100%'
            }}
          >
            <div
              style={{
                flex: '1 1 0',
                display: 'flex',
                flexDirection: 'column',
                gap: pxToRem(2)
              }}
            >
              <div
                style={{
                  color: 'var(--Color-text-normal, #161616)',
                  fontSize: pxToRem(20),
                  fontFamily: 'Pretendard, sans-serif',
                  fontWeight: '600',
                  lineHeight: pxToRem(30),
                  letterSpacing: '-0.02em'
                }}
              >
                특별 포인트 지급하기
              </div>
              {templateTitle ? (
                <div
                  style={{
                    color: 'var(--Color-text-assistive-strong, #4C4C4C)',
                    fontSize: pxToRem(14),
                    fontWeight: '500',
                    lineHeight: pxToRem(21)
                  }}
                >
                  {templateTitle}
                  {templatePointAmount != null ? ` · ${templatePointAmount}P` : ''}
                </div>
              ) : null}
            </div>
            <div
              onClick={onClose}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClose();
                }
              }}
              aria-label="닫기"
              style={{
                padding: pxToRem(4),
                borderRadius: pxToRem(8),
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer',
                flexShrink: 0
              }}
            >
              <CloseIcon width={24} height={24} color="var(--Color-text-normal, #161616)" />
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: pxToRem(24),
              width: '100%'
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: pxToRem(12),
                width: '100%'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: pxToRem(8),
                  width: '100%'
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: pxToRem(8)
                  }}
                >
                  <span
                    style={{
                      color: 'var(--Color-text-normal, #161616)',
                      fontSize: pxToRem(14),
                      fontWeight: '600',
                      lineHeight: pxToRem(21)
                    }}
                  >
                    학생 선택
                  </span>
                  <span
                    style={{
                      color: 'var(--Color-text-assistive-strong, #4C4C4C)',
                      fontSize: pxToRem(13),
                      fontWeight: '500'
                    }}
                  >
                    {selectedStudentIds.length}명 선택
                  </span>
                </div>
                <div style={{ display: 'flex', gap: pxToRem(12), flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    disabled={students.length === 0 || allSelected}
                    style={{
                      border: 'none',
                      background: 'none',
                      padding: 0,
                      color:
                        students.length === 0 || allSelected
                          ? 'var(--Color-text-assistive, #989898)'
                          : 'var(--Color-fill-brand-color, #FF6F0F)',
                      fontSize: pxToRem(13),
                      fontFamily: 'Pretendard, sans-serif',
                      fontWeight: '500',
                      cursor: students.length === 0 || allSelected ? 'default' : 'pointer',
                      textDecoration: 'underline',
                      textUnderlineOffset: pxToRem(2)
                    }}
                  >
                    전체 선택
                  </button>
                  <button
                    type="button"
                    onClick={handleClearSelection}
                    disabled={selectedStudentIds.length === 0}
                    style={{
                      border: 'none',
                      background: 'none',
                      padding: 0,
                      color:
                        selectedStudentIds.length === 0
                          ? 'var(--Color-text-assistive, #989898)'
                          : 'var(--Color-text-assistive-strong, #4C4C4C)',
                      fontSize: pxToRem(13),
                      fontFamily: 'Pretendard, sans-serif',
                      fontWeight: '500',
                      cursor: selectedStudentIds.length === 0 ? 'default' : 'pointer',
                      textDecoration: 'underline',
                      textUnderlineOffset: pxToRem(2)
                    }}
                  >
                    선택 해제
                  </button>
                </div>
              </div>

              <div
                role="group"
                aria-label="특별 포인트를 받을 학생 목록"
                style={{
                  width: '100%',
                  maxHeight: pxToRem(260),
                  overflowY: 'auto',
                  border: '1px solid var(--Color-line-normal, #F0F0F0)',
                  borderRadius: pxToRem(8),
                  padding: pxToRem(8)
                }}
              >
                {students.length === 0 ? (
                  <div
                    style={{
                      padding: pxToRem(12),
                      fontSize: pxToRem(14),
                      color: 'var(--Color-text-assistive-strong, #4C4C4C)'
                    }}
                  >
                    등록된 학생이 없습니다.
                  </div>
                ) : (
                  students.map((student) => {
                    const checked = selectedStudentIds.includes(student.uid);
                    const label = `${student.number != null ? `${student.number}번 ` : ''}${student.name}`;
                    return (
                      <label
                        key={student.uid}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: pxToRem(10),
                          padding: `${pxToRem(8)} ${pxToRem(10)}`,
                          borderRadius: pxToRem(6),
                          cursor: 'pointer',
                          backgroundColor: checked ? 'var(--Color-fill-hover, #F5F5F5)' : 'transparent'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleStudent(student.uid)}
                          style={{
                            width: pxToRem(18),
                            height: pxToRem(18),
                            flexShrink: 0,
                            cursor: 'pointer',
                            accentColor: 'var(--Color-fill-brand-color, #FF6F0F)'
                          }}
                        />
                        <span
                          style={{
                            fontSize: pxToRem(14),
                            color: 'var(--Color-text-normal, #161616)',
                            lineHeight: pxToRem(21),
                            wordBreak: 'break-word'
                          }}
                        >
                          {label}
                        </span>
                      </label>
                    );
                  })
                )}
              </div>
            </div>

            <div
              style={{
                height: pxToRem(84),
                width: '100%'
              }}
            >
              <TextAreaField
                value={reason}
                onChange={setReason}
                placeholder="특별 포인트 부여 사유를 입력해 주세요"
                width="100%"
                height={pxToRem(84)}
                size="small"
              />
            </div>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            gap: pxToRem(8),
            width: '100%'
          }}
        >
          <Button
            width={pxToRem(124)}
            height={pxToRem(48)}
            variant="cancel"
            onClick={onClose}
            disabled={isSubmitting}
            style={{
              backgroundColor: '#E9E9E9',
              color: 'var(--Color-text-normal, #161616)',
              fontSize: pxToRem(14),
              fontFamily: 'Pretendard, sans-serif',
              fontWeight: '500',
              lineHeight: pxToRem(21)
            }}
          >
            취소
          </Button>
          <Button
            width={pxToRem(262)}
            height={pxToRem(48)}
            variant="submit"
            onClick={handleSave}
            disabled={isSaveDisabled}
            style={{
              backgroundColor: 'var(--Color-fill-normal, #293044)',
              color: 'var(--Color-text-inverse, white)',
              fontSize: pxToRem(14),
              fontFamily: 'Pretendard, sans-serif',
              fontWeight: '500',
              lineHeight: pxToRem(21)
            }}
          >
            {isSubmitting
              ? '처리 중...'
              : selectedStudentIds.length > 0
                ? `포인트 지급 (${selectedStudentIds.length}명)`
                : '포인트 지급'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DistributePointsDialog;
